import { useTheme } from '@mui/material/styles';
import { FONTS, FEATURES } from '../../../config/app-config';
// File: src/components/navigation/toc/TocHeaderBar.jsx

import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Toolbar,
    IconButton,
    Button,
    LinearProgress,
    Typography,
    Divider,
    Tooltip
} from '@mui/material';
import ReleaseSelectors from '../header/ReleaseSelectors';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { SIDENAV_MODES } from "../SideNavigation";

const formatTime = (ms) => {
    const s = Math.max(0, Math.round(ms / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export default function TocHeaderBar({
    mobile = false,
    selectedIds,
    jobActive,
    jobProgress,
    jobResultUrl,
    onOpenPdf,
    onPrintClick,
    onCancelClick,
    onConfirmClick,
    toggleDarkMode,
    mode,          // 'print' | 'llm'
    isAdmin,
    onProcessClick,
    onSelectAll,
    onClearSelection,
    selectingAll,
}) {
    const theme = useTheme();
    const darkMode = theme.palette.mode === 'dark';
    const startTimeRef = useRef(null);
    const [eta, setEta] = useState('');

    /* ETA --------------------------------------------------- */
    useEffect(() => {
        if (jobActive) {
            startTimeRef.current = Date.now();
            setEta('');
        }
    }, [jobActive]);

    useEffect(() => {
        if (jobActive && jobProgress > 0 && startTimeRef.current) {
            const elapsed = Date.now() - startTimeRef.current;
            const total = (elapsed * 100) / jobProgress;
            setEta(formatTime(total - elapsed));
        }
    }, [jobProgress, jobActive]);

    function onOpenPdfClick() {
        window.open(jobResultUrl, '_blank');
    }

    const progress = jobProgress || 0;
    const loadingText = progress < 10 ? 'You are currently waiting in queue…'
        : progress < 95 ? 'Processing…'
            : 'Finishing up…';

    const iconSize = 24;

    /* Panel nach Abschluss geöffnet lassen, damit Open/Download sichtbar sind */
    const showPanel =
        (mode === 'print' && (jobActive || !!jobResultUrl)) ||
        (mode === 'llm' && jobActive);

    // Buttons for action selection - Dark Mode/ PDF / LLM
    const toolButtons = (
        <>
            {/* Dark / Light */}
            {FEATURES.themes && (<Tooltip
                title={darkMode ? 'Switch to Lightmode' : 'Switch to Darkmode'}
                arrow
            >
                <span>
                    <IconButton
                        aria-label={darkMode ? 'Switch to Lightmode' : 'Switch to Darkmode'}
                        onClick={toggleDarkMode}
                        sx={{ fontSize: iconSize, color: darkMode ? '#fff' : 'inherit' }}
                    >
                        <Box sx={{ overflow: 'hidden', width: iconSize, height: iconSize }}>
                            <Box
                                sx={{
                                    transition: 'transform .4s ease',
                                    transform: darkMode ? 'translateY(-30px)' : 'translateY(0)',
                                }}
                            >
                                {/* Reihenfolge getauscht: zuerst Mond, dann Sonne.
                                              Dadurch sieht man im Lightmode den Mond (zu Darkmode wechseln)
                                              und im Darkmode die Sonne (zu Lightmode wechseln). */}
                                <DarkModeIcon fontSize="inherit" />
                                <LightModeIcon fontSize="inherit" />
                            </Box>
                        </Box>
                    </IconButton>
                </span>
            </Tooltip>)}

            {/* Print */}
            {FEATURES.pdf && (
                <Tooltip title="Print as PDF" arrow>
                    <span>
                        <IconButton
                            aria-label="Print as PDF"
                            onClick={onPrintClick}
                            sx={{ fontSize: iconSize, color: darkMode ? '#fff' : 'inherit' }}
                        >
                            <PrintIcon fontSize="inherit" />
                        </IconButton>
                    </span>
                </Tooltip>
            )}

            {/* LLM – only admins (Deactivated if not supported by OEM) */}
            {isAdmin && FEATURES.llm && (
                <Tooltip title="Process Context in Engineering-Assistant" arrow>
                    <span>
                        <IconButton
                            aria-label="Process Context in Engineering-Assistant"
                            onClick={onProcessClick}
                            sx={{ fontSize: iconSize, color: darkMode ? '#fff' : 'inherit' }}
                        >
                            <AutoAwesomeIcon fontSize="inherit" />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </>
    );

    const toolButtonSelection = (
        <>
            {/* Zurück */}
            <Button
                color="inherit"
                size="small"
                onClick={onCancelClick}
                startIcon={<ArrowBackIcon />}
                sx={{
                    textTransform: 'none',
                    fontFamily: FONTS.body,
                    fontSize: 10,
                    mr: 1,
                    color: darkMode ? '#fff' : 'inherit',
                }}
            >
                Cancel
            </Button>

            <Button
                color="inherit"
                size="small"
                onClick={onClearSelection}
                disabled={jobActive || selectingAll || selectedIds.length === 0}
                sx={{
                    textTransform: 'none',
                    fontFamily: FONTS.body,
                    fontSize: 10,
                    mr: 0.5,
                    color: darkMode ? '#fff' : 'inherit',
                }}
            >
                Unselect All
            </Button>

            <Button
                color="inherit"
                size="small"
                onClick={onSelectAll}
                disabled={jobActive || selectingAll}
                sx={{
                    textTransform: 'none',
                    fontFamily: FONTS.body,
                    fontSize: 10,
                    mr: 0.5,
                    color: darkMode ? '#fff' : 'inherit',
                }}
            >
                {selectingAll ? 'Selecting…' : 'Select All'}
            </Button>

            <Box sx={{ flexGrow: 1 }} />

            {/* Aktions-Button */}
            {mode === 'print' ? (
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<PrintIcon fontSize="inherit" />}
                    onClick={onConfirmClick}
                    disabled={jobActive || selectedIds.length === 0}
                    sx={{
                        textTransform: 'none',
                        fontFamily: FONTS.body,
                        fontSize: 10,
                        bgcolor: theme.palette.primary.main,
                        color: '#fff',
                        px: 1,
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: theme.palette.primary.main,
                            boxShadow: 'none',
                        },
                    }}
                >
                    Create PDF
                </Button>
            ) : null}
        </>
    );

    return (
        <Box
            sx={(t) => {
                let h = '56px';   // Base height for the Header Bar

                if (jobActive) {
                    h = '160px';
                } else if (jobResultUrl) {
                    h = '200px';
                }

                return {
                    position: 'sticky',
                    top: 0,
                    zIndex: t.zIndex.drawer + 1,
                    bgcolor: 'background.default',
                    boxShadow: t.shadows[4],
                    width: '100%',
                    height: h,
                };
            }}
        >
            <Toolbar
                disableGutters
                sx={{
                    px: { xs: 2 },
                    flexWrap: mobile ? 'wrap' : 'nowrap',
                    gap: mobile && mode === SIDENAV_MODES.none ? 0.5 : 0,
                    minHeight: { xs: 56, sm: 64 },
                }}
            >
                {/* Display normal button elements if no selection is active */}
                {/* Display selection elements if a selection by the user is occuring*/}
                {mode === SIDENAV_MODES.none ? (
                    <>
                        {mobile && [
                            <ReleaseSelectors />,
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{
                                    display: { xs: 'block' },
                                    transform: 'translateY(40%)',
                                    height: '32px',
                                    mx: 1,
                                    bgcolor: theme => theme.palette.divider,
                                    width: '1px'
                                }} />
                        ]
                        }
                        {toolButtons}
                    </>
                ) : toolButtonSelection}
            </Toolbar>

            {/* Fortschritts-/Ergebnis-Leiste — egal ob PDF oder LLM */}
            {showPanel && (
                <Box sx={{ px: 2, pb: 1 }}>
                    <Divider sx={{ mb: 1, borderColor: darkMode ? '#444' : undefined }} />

                    <Typography
                        sx={{
                            fontFamily: FONTS.body,
                            fontWeight: 600,
                            mb: 1,
                            color: 'text.primary'
                        }}
                    >
                        {mode === SIDENAV_MODES.print
                            ? (jobActive ? 'Generating PDF' : 'PDF ready')
                            : 'Processing with LLM'}
                    </Typography>

                    <Typography
                        sx={{ fontFamily: FONTS.body, mb: 1, color: 'text.primary' }}
                    >
                        {jobActive
                            ? loadingText
                            : (mode === SIDENAV_MODES.print && jobResultUrl ? 'Your file is ready — download it below. The link is valid for 10 minutes.' : '')}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {/* links: nur zeigen, wenn Fortschritt sichtbar ist */}
                        {jobActive && (
                            <Box sx={{ flex: 1, mr: 1 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: 'progress.main',
                                        },
                                    }}
                                />
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                            {jobActive && (
                                <>
                                    <Typography
                                        sx={{
                                            minWidth: 35,
                                            fontFamily: FONTS.body,
                                            color: darkMode ? '#fff' : undefined
                                        }}
                                    >
                                        {`${progress} %`}
                                    </Typography>
                                    {eta && (
                                        <Typography
                                            sx={{
                                                ml: 2,
                                                fontFamily: FONTS.body,
                                                mr: '1px',
                                                color: darkMode ? '#fff' : undefined
                                            }}
                                        >
                                            | ETA: {eta}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Box>

                        {/* Aktionen, wenn fertig */}
                        {jobResultUrl && !jobActive && (
                            <>
                                <Button
                                    href={null}
                                    onClick={onOpenPdfClick}
                                    view
                                    sx={{
                                        textTransform: 'none',
                                        backgroundColor: theme.palette.primary.main,
                                        color: '#fff',
                                        fontFamily: FONTS.body,
                                    }}
                                >
                                    View PDF File
                                </Button>
                            </>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

